import React, { useState } from 'react';
import './ResumePreviewModal.css';

function ResumePreviewModal({ parsedResume, onConfirm, onClose, confirming = false }) {
  const [activeTab, setActiveTab] = useState('contact');

  if (!parsedResume) return null;

  const { contact_info, education, experience, projects, skills } = parsedResume;

  const tabs = [
    { id: 'contact', label: 'Contact', count: null },
    { id: 'experience', label: 'Experience', count: experience?.length || 0 },
    { id: 'education', label: 'Education', count: education?.length || 0 },
    { id: 'projects', label: 'Projects', count: projects?.length || 0 },
    { id: 'skills', label: 'Skills', count: null },
  ];

  const getTotalSkillsCount = () => {
    if (!skills) return 0;
    return Object.values(skills).reduce((acc, arr) => acc + (arr?.length || 0), 0);
  };

  return (
    <div className="resume-preview-overlay" onClick={onClose}>
      <div className="resume-preview-modal" onClick={(e) => e.stopPropagation()}>
        <div className="preview-header">
          <div className="preview-header-content">
            <div className="preview-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </div>
            <div>
              <h2 className="preview-title">Resume Parsed Successfully</h2>
              <p className="preview-subtitle">Review the extracted information below</p>
            </div>
          </div>
          <button className="preview-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <div className="preview-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`preview-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
              {tab.count !== null && tab.count > 0 && (
                <span className="tab-count">{tab.count}</span>
              )}
              {tab.id === 'skills' && getTotalSkillsCount() > 0 && (
                <span className="tab-count">{getTotalSkillsCount()}</span>
              )}
            </button>
          ))}
        </div>

        <div className="preview-content">
          {activeTab === 'contact' && (
            <div className="preview-section">
              <div className="contact-grid">
                <div className="contact-item">
                  <label>Name</label>
                  <p>{contact_info?.full_name || 'Not found'}</p>
                </div>
                <div className="contact-item">
                  <label>Email</label>
                  <p>{contact_info?.email || 'Not found'}</p>
                </div>
                <div className="contact-item">
                  <label>Phone</label>
                  <p>{contact_info?.phone || 'Not found'}</p>
                </div>
                <div className="contact-item">
                  <label>Location</label>
                  <p>{contact_info?.location || 'Not specified'}</p>
                </div>
                {contact_info?.linkedin_url && (
                  <div className="contact-item">
                    <label>LinkedIn</label>
                    <p className="link-text">{contact_info.linkedin_url}</p>
                  </div>
                )}
                {contact_info?.github_url && (
                  <div className="contact-item">
                    <label>GitHub</label>
                    <p className="link-text">{contact_info.github_url}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'experience' && (
            <div className="preview-section">
              {experience?.length > 0 ? (
                <div className="experience-list">
                  {experience.map((exp, idx) => (
                    <div key={idx} className="experience-item">
                      <div className="experience-header">
                        <h4>{exp.job_title}</h4>
                        <span className="experience-dates">{exp.start_date} - {exp.end_date}</span>
                      </div>
                      <p className="experience-company">{exp.company_name} • {exp.location}</p>
                      {exp.bullets?.length > 0 && (
                        <ul className="experience-bullets">
                          {exp.bullets.map((bullet, bIdx) => (
                            <li key={bIdx}>{bullet}</li>
                          ))}
                        </ul>
                      )}
                      {exp.technologies?.length > 0 && (
                        <div className="experience-tech">
                          {exp.technologies.map((tech, tIdx) => (
                            <span key={tIdx} className="tech-tag">{tech}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="empty-state">No work experience found in resume</p>
              )}
            </div>
          )}

          {activeTab === 'education' && (
            <div className="preview-section">
              {education?.length > 0 ? (
                <div className="education-list">
                  {education.map((edu, idx) => (
                    <div key={idx} className="education-item">
                      <div className="education-header">
                        <h4>{edu.school_name}</h4>
                        <span className="education-date">{edu.graduation_date}</span>
                      </div>
                      <p className="education-degree">
                        {edu.degree} in {edu.major}
                        {edu.minor && `, Minor in ${edu.minor}`}
                      </p>
                      {edu.gpa && <p className="education-gpa">GPA: {edu.gpa}</p>}
                      {edu.honors?.length > 0 && (
                        <div className="education-honors">
                          {edu.honors.map((honor, hIdx) => (
                            <span key={hIdx} className="honor-tag">{honor}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="empty-state">No education found in resume</p>
              )}
            </div>
          )}

          {activeTab === 'projects' && (
            <div className="preview-section">
              {projects?.length > 0 ? (
                <div className="projects-list">
                  {projects.map((proj, idx) => (
                    <div key={idx} className="project-item">
                      <div className="project-header">
                        <h4>{proj.project_name}</h4>
                        <span className="project-context">{proj.context}</span>
                      </div>
                      <p className="project-date">{proj.date}</p>
                      {proj.bullets?.length > 0 && (
                        <ul className="project-bullets">
                          {proj.bullets.map((bullet, bIdx) => (
                            <li key={bIdx}>{bullet}</li>
                          ))}
                        </ul>
                      )}
                      {proj.technologies?.length > 0 && (
                        <div className="project-tech">
                          {proj.technologies.map((tech, tIdx) => (
                            <span key={tIdx} className="tech-tag">{tech}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="empty-state">No projects found in resume</p>
              )}
            </div>
          )}

          {activeTab === 'skills' && (
            <div className="preview-section">
              {getTotalSkillsCount() > 0 ? (
                <div className="skills-grid">
                  {skills?.languages?.length > 0 && (
                    <div className="skill-category">
                      <h5>Languages</h5>
                      <div className="skill-tags">
                        {skills.languages.map((skill, idx) => (
                          <span key={idx} className="skill-tag">{skill}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {skills?.frontend?.length > 0 && (
                    <div className="skill-category">
                      <h5>Frontend</h5>
                      <div className="skill-tags">
                        {skills.frontend.map((skill, idx) => (
                          <span key={idx} className="skill-tag">{skill}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {skills?.backend?.length > 0 && (
                    <div className="skill-category">
                      <h5>Backend</h5>
                      <div className="skill-tags">
                        {skills.backend.map((skill, idx) => (
                          <span key={idx} className="skill-tag">{skill}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {skills?.databases?.length > 0 && (
                    <div className="skill-category">
                      <h5>Databases</h5>
                      <div className="skill-tags">
                        {skills.databases.map((skill, idx) => (
                          <span key={idx} className="skill-tag">{skill}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {skills?.cloud_devops?.length > 0 && (
                    <div className="skill-category">
                      <h5>Cloud & DevOps</h5>
                      <div className="skill-tags">
                        {skills.cloud_devops.map((skill, idx) => (
                          <span key={idx} className="skill-tag">{skill}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {skills?.tools?.length > 0 && (
                    <div className="skill-category">
                      <h5>Tools</h5>
                      <div className="skill-tags">
                        {skills.tools.map((skill, idx) => (
                          <span key={idx} className="skill-tag">{skill}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="empty-state">No skills found in resume</p>
              )}
            </div>
          )}
        </div>

        <div className="preview-footer">
          <p className="preview-note">
            This will be saved as your master resume. You can edit it later in your profile.
          </p>
          <div className="preview-actions">
            <button className="btn-secondary" onClick={onClose} disabled={confirming}>
              Skip for now
            </button>
            <button className="btn-primary" onClick={onConfirm} disabled={confirming}>
              {confirming ? (
                <>
                  <span className="btn-spinner" />
                  Saving...
                </>
              ) : (
                'Save as Master Resume'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResumePreviewModal;
